import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Flex,
  Input,
  Modal,
  Spin,
  Table,
  Tag,
  Typography,
} from '@duxton-web/ui';
import { ColumnsType } from '@duxton-web/ui/es/table';

import { VariableState } from '@/constants';
import {
  FetchStatus,
  OperationStatus,
  VariableRow,
} from '@/features/admin/types';
import AppService, { VariableInfo } from '@/lib/App';
import { extractErrorMessage } from '@/lib/utils/error';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_VARIABLES = 200;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BulkDeletionItem {
  name: string;
  archiveStatus: OperationStatus;
  archiveError?: string;
  isArchived: boolean;
  selectedForDeletion: boolean;
  deleteStatus?: OperationStatus;
  deleteError?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ARCHIVE_STATUS_TAG: Record<OperationStatus, React.ReactNode> = {
  pending: (
    <Tag color="processing" icon={<Spin size="small" />}>
      In Progress
    </Tag>
  ),
  success: <Tag color="success">Done</Tag>,
  error: <Tag color="error">Failed</Tag>,
};

const DELETE_STATUS_TAG: Record<OperationStatus, React.ReactNode> = {
  pending: (
    <Tag color="processing" icon={<Spin size="small" />}>
      In Progress
    </Tag>
  ),
  success: <Tag color="success">Deleted</Tag>,
  error: <Tag color="error">Failed</Tag>,
};

// ---------------------------------------------------------------------------
// BulkDeletionTab
// ---------------------------------------------------------------------------

const BulkDeletionTab: React.FC = () => {
  const [inputValue, setInputValue] = useState('');

  // Step 1: fetching variable info for preview
  const [fetchStatus, setFetchStatus] = useState<FetchStatus>('idle');
  const [variableRows, setVariableRows] = useState<VariableRow[]>([]);

  // Selection in preview table: tracked by name so it survives search filtering
  const [selectedPreviewKeys, setSelectedPreviewKeys] = useState<Set<string>>(
    new Set(),
  );

  // Search within the preview table
  const [searchText, setSearchText] = useState('');

  // Archive confirmation modal
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);

  // Archive + delete results
  const [items, setItems] = useState<BulkDeletionItem[]>([]);
  const [isArchiving, setIsArchiving] = useState(false);

  // Delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Ref to track latest items value (avoids stale closure in runDeletion)
  const itemsRef = useRef<BulkDeletionItem[]>([]);
  itemsRef.current = items;

  // -------------------------------------------------------------------------
  // Parse names from textarea
  // -------------------------------------------------------------------------
  const rawNameCount = useMemo(
    () =>
      [
        ...new Set(
          inputValue
            .split(/[\n,]+/)
            .map((s) => s.trim())
            .filter(Boolean),
        ),
      ].length,
    [inputValue],
  );

  const parsedNames = useMemo(() => {
    const names = [
      ...new Set(
        inputValue
          .split(/[\n,]+/)
          .map((s) => s.trim())
          .filter(Boolean),
      ),
    ];
    return names.slice(0, MAX_VARIABLES);
  }, [inputValue]);

  // -------------------------------------------------------------------------
  // Archive execution
  // -------------------------------------------------------------------------
  const updateItem = useCallback(
    (name: string, updates: Partial<BulkDeletionItem>) => {
      setItems((prev) =>
        prev.map((item) =>
          item.name === name ? { ...item, ...updates } : item,
        ),
      );
    },
    [],
  );

  const runArchival = useCallback(
    async (namesToArchive: string[]) => {
      if (namesToArchive.length === 0 || isArchiving) return;

      const initialItems: BulkDeletionItem[] = namesToArchive.map((name) => ({
        name,
        archiveStatus: 'pending',
        isArchived: false,
        selectedForDeletion: false,
      }));
      setItems(initialItems);
      setIsArchiving(true);

      await Promise.allSettled(
        namesToArchive.map(async (name) => {
          let archiveError: string | undefined;

          try {
            await AppService.planner.archiveVariable(name);
          } catch (err: unknown) {
            archiveError = extractErrorMessage(err);
          }

          // Fetch fresh variable detail regardless of archive success/failure
          let isArchived = false;
          try {
            const fresh = (await AppService.planner.fetchVariable(
              name,
            )) as VariableInfo;
            isArchived = fresh?.state === VariableState.ARCHIVED;
          } catch {
            // If fetch fails, treat as not archived
          }

          const archiveStatus: OperationStatus = archiveError
            ? 'error'
            : isArchived
              ? 'success'
              : 'error';
          const finalArchiveError =
            archiveError ??
            (!isArchived ? 'Variable did not reach archived state' : undefined);

          updateItem(name, {
            archiveStatus,
            archiveError: finalArchiveError,
            isArchived,
            selectedForDeletion: isArchived,
          });
        }),
      );

      setIsArchiving(false);
    },
    [updateItem, isArchiving],
  );

  const handleProceedArchival = useCallback(() => {
    runArchival([...selectedPreviewKeys]);
  }, [runArchival, selectedPreviewKeys]);

  // -------------------------------------------------------------------------
  // Delete execution
  // -------------------------------------------------------------------------
  const runDeletion = useCallback(async () => {
    const toDelete = itemsRef.current.filter(
      (item) => item.selectedForDeletion,
    );
    if (toDelete.length === 0) return;

    setIsDeleting(true);

    // Mark all selected items as pending delete
    toDelete.forEach(({ name }) => {
      updateItem(name, { deleteStatus: 'pending' });
    });

    await Promise.allSettled(
      toDelete.map(({ name }) =>
        AppService.planner
          .deleteVariable(name)
          .then(() => updateItem(name, { deleteStatus: 'success' }))
          .catch((err: unknown) =>
            updateItem(name, {
              deleteStatus: 'error',
              deleteError: extractErrorMessage(err),
            }),
          ),
      ),
    );

    setIsDeleting(false);
  }, [updateItem]);

  // -------------------------------------------------------------------------
  // Step 1: Fetch variable info for each name
  // -------------------------------------------------------------------------
  const handleFetchVariables = useCallback(async () => {
    if (parsedNames.length === 0) return;

    setFetchStatus('loading');
    setVariableRows([]);
    setSelectedPreviewKeys(new Set());
    setItems([]);
    setSearchText('');

    const results = await Promise.allSettled(
      parsedNames.map((name) => AppService.planner.fetchVariable(name)),
    );

    const rows: VariableRow[] = results.map((result, idx) => {
      const name = parsedNames[idx];
      if (result.status === 'fulfilled') {
        const info = result.value as VariableInfo;
        return {
          name,
          state: info?.state ?? 'unknown',
          lastTriggeredAt: info?.xmsUpdatedAt,
        };
      }
      return { name, state: 'unknown' };
    });

    setVariableRows(rows);
    // Pre-select only non-active variables; active ones should be unchecked by default
    setSelectedPreviewKeys(
      new Set(
        rows.filter((r) => r.state !== VariableState.ACTIVE).map((r) => r.name),
      ),
    );

    const allInactive = rows.every((r) => r.state === VariableState.INACTIVE);
    if (allInactive) {
      // All inactive — archive immediately without showing table or modal
      setFetchStatus('idle');
      runArchival(parsedNames);
    } else {
      setFetchStatus('done');
    }
  }, [parsedNames, runArchival]);

  // -------------------------------------------------------------------------
  // Derived state
  // -------------------------------------------------------------------------
  const filteredRows = useMemo(() => {
    if (!searchText.trim()) return variableRows;
    const lower = searchText.toLowerCase();
    return variableRows.filter((r) => r.name.toLowerCase().includes(lower));
  }, [variableRows, searchText]);

  const handleArchiveModalConfirm = useCallback(() => {
    setArchiveModalOpen(false);
    setFetchStatus('idle');
    handleProceedArchival();
  }, [handleProceedArchival]);

  const handleDeleteModalConfirm = useCallback(() => {
    setDeleteModalOpen(false);
    runDeletion();
  }, [runDeletion]);

  // -------------------------------------------------------------------------
  // Row selection — preserves selection state across search changes
  // -------------------------------------------------------------------------
  const rowSelection = {
    columnWidth: 40,
    selectedRowKeys: filteredRows
      .filter((r) => selectedPreviewKeys.has(r.name))
      .map((r) => r.name),
    onChange: (_keys: React.Key[], selectedRows: VariableRow[]) => {
      const visibleNames = new Set(filteredRows.map((r) => r.name));
      setSelectedPreviewKeys((prev) => {
        const next = new Set(prev);
        visibleNames.forEach((n) => next.delete(n));
        selectedRows.forEach((r) => next.add(r.name));
        return next;
      });
    },
  };

  // -------------------------------------------------------------------------
  // Derived flags
  // -------------------------------------------------------------------------
  const showPreviewTable = fetchStatus === 'done' && variableRows.length > 0;
  const isArchivalComplete = !isArchiving && items.length > 0;
  const selectedForDeletionCount = items.filter(
    (i) => i.selectedForDeletion,
  ).length;
  const hasAnyArchived = items.some((i) => i.isArchived);

  const deletionStarted = items.some((i) => i.deleteStatus !== undefined);

  // Delete phase: show delete button only after all archives complete;
  // remains visible while there are retryable (failed) items
  const hasDeletableSelected = items.some(
    (i) => i.selectedForDeletion && i.deleteStatus !== 'success',
  );
  const showDeleteButton =
    isArchivalComplete && !isDeleting && hasDeletableSelected;

  const archiveSuccessCount = items.filter(
    (i) => i.archiveStatus === 'success',
  ).length;
  const archiveFailedItems = items.filter((i) => i.archiveStatus === 'error');
  const deleteSuccessCount = items.filter(
    (i) => i.deleteStatus === 'success',
  ).length;
  const deleteFailedItems = items.filter((i) => i.deleteStatus === 'error');
  const isDeletionComplete = !isDeleting && deletionStarted;

  // -------------------------------------------------------------------------
  // Columns
  // -------------------------------------------------------------------------
  const previewColumns: ColumnsType<VariableRow> = [
    {
      title: 'Variable Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Variable State',
      dataIndex: 'state',
      key: 'state',
      width: 120,
      render: (state: string) => {
        switch (state) {
          case VariableState.ACTIVE:
            return <Tag color="success">Active</Tag>;
          case VariableState.INACTIVE:
            return <Tag color="default">Inactive</Tag>;
          case VariableState.ARCHIVED:
            return <Tag color="blue">Archived</Tag>;
          case 'unknown':
            return <Tag color="warning">Unknown</Tag>;
          default:
            return <Tag>{state}</Tag>;
        }
      },
    },
    {
      title: 'Last Triggered At',
      dataIndex: 'lastTriggeredAt',
      key: 'lastTriggeredAt',
      width: 180,
      render: (date?: Date | null) =>
        date ? new Date(date).toLocaleString() : '—',
    },
  ];

  const resultsColumns = useMemo<ColumnsType<BulkDeletionItem>>(
    () => [
      ...(hasAnyArchived
        ? [
            {
              title: '',
              key: 'select',
              width: 40,
              render: (_: unknown, record: BulkDeletionItem) =>
                record.isArchived ? (
                  <Checkbox
                    checked={record.selectedForDeletion}
                    disabled={record.deleteStatus === 'success' || isDeleting}
                    onChange={(e) =>
                      updateItem(record.name, {
                        selectedForDeletion: e.target.checked,
                      })
                    }
                  />
                ) : null,
            },
          ]
        : []),
      {
        title: 'Variable Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Archive Status',
        dataIndex: 'archiveStatus',
        key: 'archiveStatus',
        render: (status: OperationStatus) => ARCHIVE_STATUS_TAG[status],
      },
      {
        title: 'Archive Error',
        dataIndex: 'archiveError',
        key: 'archiveError',
        render: (errorMsg?: string) => errorMsg ?? '—',
      },
      ...(deletionStarted
        ? [
            {
              title: 'Delete Status',
              dataIndex: 'deleteStatus',
              key: 'deleteStatus',
              render: (status?: OperationStatus) =>
                status ? DELETE_STATUS_TAG[status] : '—',
            },
            {
              title: 'Delete Error',
              dataIndex: 'deleteError',
              key: 'deleteError',
              render: (errorMsg?: string) => errorMsg ?? '—',
            },
          ]
        : []),
    ],
    [hasAnyArchived, deletionStarted, updateItem, isDeleting],
  );

  return (
    <Flex vertical gap={16}>
      <Input.TextArea
        placeholder="Enter variable names, one per line or comma-separated"
        rows={4}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          setFetchStatus('idle');
          setVariableRows([]);
          setItems([]);
        }}
        disabled={isArchiving || fetchStatus === 'loading' || isDeleting}
      />
      {rawNameCount > MAX_VARIABLES && (
        <Alert
          type="warning"
          message={`Only the first ${MAX_VARIABLES} variables will be processed. You entered ${rawNameCount}.`}
        />
      )}
      <Flex justify="flex-end">
        <Button
          size="small"
          type="primary"
          onClick={handleFetchVariables}
          loading={fetchStatus === 'loading'}
          disabled={parsedNames.length === 0 || isArchiving || isDeleting}
        >
          Load Variables
        </Button>
      </Flex>

      {showPreviewTable && <Divider style={{ margin: 0 }} />}

      {/* Preview table: shown after variable info is loaded */}
      {showPreviewTable && (
        <Flex vertical gap={8}>
          <Flex justify="space-between" align="center">
            <Input
              placeholder="Search variable names"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 280 }}
              allowClear
            />
            <Button
              danger
              type="primary"
              size="small"
              disabled={selectedPreviewKeys.size === 0 || isArchiving}
              loading={isArchiving}
              onClick={() => setArchiveModalOpen(true)}
            >
              Proceed with Archival
            </Button>
          </Flex>

          <Table
            dataSource={filteredRows}
            columns={previewColumns}
            rowKey="name"
            pagination={false}
            size="small"
            virtual
            scroll={{ y: 320 }}
            rowSelection={rowSelection}
          />
        </Flex>
      )}

      {/* Archive + delete results table */}
      {items.length > 0 && (
        <Table
          dataSource={items}
          columns={resultsColumns}
          rowKey="name"
          pagination={false}
          size="small"
        />
      )}

      {/* Delete button — visible after archiving completes, allows retry of failed deletions */}
      {showDeleteButton && (
        <Flex justify="flex-end">
          <Button
            danger
            type="primary"
            size="small"
            disabled={selectedForDeletionCount === 0}
            onClick={() => setDeleteModalOpen(true)}
          >
            Delete Selected ({selectedForDeletionCount})
          </Button>
        </Flex>
      )}

      {/* Archive summary */}
      {isArchivalComplete && !deletionStarted && (
        <Alert
          type="info"
          message={
            <Flex vertical gap={4}>
              <Typography.Text>
                {archiveSuccessCount} archival request
                {archiveSuccessCount !== 1 ? 's' : ''} successful.
              </Typography.Text>
              <Typography.Text>
                {archiveFailedItems.length} archival request
                {archiveFailedItems.length !== 1 ? 's' : ''} failed.
              </Typography.Text>
            </Flex>
          }
        />
      )}

      {/* Deletion summary */}
      {isDeletionComplete && (
        <Alert
          type={deleteFailedItems.length > 0 ? 'warning' : 'success'}
          message={
            <Flex vertical gap={4}>
              <Typography.Text>
                {deleteSuccessCount} deletion request
                {deleteSuccessCount !== 1 ? 's' : ''} successful.
              </Typography.Text>
              {deleteFailedItems.length > 0 && (
                <Flex vertical gap={2}>
                  <Typography.Text>
                    {deleteFailedItems.length} deletion request
                    {deleteFailedItems.length !== 1 ? 's' : ''} failed.
                  </Typography.Text>
                  <Typography.Text strong>Failed variables:</Typography.Text>
                  {deleteFailedItems.map((item) => (
                    <Typography.Text key={item.name}>
                      {item.name}
                      {item.deleteError ? ` — ${item.deleteError}` : ''}
                    </Typography.Text>
                  ))}
                </Flex>
              )}
            </Flex>
          }
        />
      )}

      {/* Archive confirmation modal */}
      <Modal
        open={archiveModalOpen}
        title="Proceed with Archival"
        centered
        onOk={handleArchiveModalConfirm}
        onCancel={() => {
          setArchiveModalOpen(false);
        }}
        okText="Confirm"
        cancelText="Cancel"
        okButtonProps={{ danger: true, size: 'small' }}
        cancelButtonProps={{ size: 'small' }}
      >
        <Typography.Text>
          Are you sure you want to archive the selected variables?
        </Typography.Text>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        open={deleteModalOpen}
        title="Confirm Deletion"
        centered
        onOk={handleDeleteModalConfirm}
        onCancel={() => setDeleteModalOpen(false)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true, size: 'small' }}
        cancelButtonProps={{ size: 'small' }}
      >
        <Flex vertical gap={8}>
          <Typography.Text>
            Are you sure you want to permanently delete the{' '}
            {selectedForDeletionCount} selected variable
            {selectedForDeletionCount !== 1 ? 's' : ''}?
          </Typography.Text>
          <Typography.Text type="danger" strong>
            This action is permanent and cannot be undone.
          </Typography.Text>
        </Flex>
      </Modal>
    </Flex>
  );
};

export default BulkDeletionTab;
